import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Professional PDF styles that can be reused across different forms
export const professionalPDFStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
    lineHeight: 1.4,
  },
  header: {
    marginBottom: 25,
    textAlign: 'center',
    borderBottom: '2pt solid #2E86C1',
    paddingBottom: 15,
  },
  headerLogo: {
    width: 80,
    height: 'auto',
    marginBottom: 10,
    alignSelf: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E86C1',
    marginBottom: 5,
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#5D6D7E',
    marginBottom: 8,
  },
  headerInfo: {
    fontSize: 9,
    color: '#7F8C8D',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 12,
    color: '#2E86C1',
    backgroundColor: '#EBF5FB',
    padding: 6,
    borderLeft: '3pt solid #2E86C1',
  },
  subsectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 12,
    marginBottom: 8,
    color: '#34495E',
    borderBottom: '1pt solid #BDC3C7',
    paddingBottom: 3,
  },
  sectionDescription: {
    fontSize: 8,
    color: '#7F8C8D',
    marginBottom: 10,
    fontStyle: 'italic',
    paddingLeft: 5,
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'flex-start',
  },
  fieldLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#34495E',
    width: '35%',
    paddingRight: 8,
  },
  fieldValue: {
    fontSize: 9,
    color: '#2C3E50',
    width: '65%',
    lineHeight: 1.3,
  },
  emptyValue: {
    fontSize: 9,
    color: '#95A5A6',
    fontStyle: 'italic',
  },
  fileValue: {
    fontSize: 9,
    color: '#E74C3C',
    fontStyle: 'italic',
  },
  currencyValue: {
    fontSize: 9,
    color: '#27AE60',
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    borderTop: '1pt solid #BDC3C7',
    paddingTop: 8,
  },
  footerText: {
    fontSize: 7,
    color: '#7F8C8D',
    marginBottom: 2,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 7,
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#7F8C8D',
  },
  applicationMeta: {
    marginTop: 15,
    padding: 8,
    backgroundColor: '#F8F9FA',
    borderRadius: 3,
    border: '1pt solid #E9ECEF',
  },
  applicationMetaWithPhoto: {
    marginTop: 15,
    padding: 8,
    backgroundColor: '#F8F9FA',
    borderRadius: 3,
    border: '1pt solid #E9ECEF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  applicantInfo: {
    flex: 1,
    paddingRight: 10,
  },
  photoSection: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 85,
  },
  passportPhoto: {
    width: 80,
    height: 100,
    objectFit: 'cover',
    border: '1pt solid #2E86C1',
    borderRadius: 2,
  },
  photoPlaceholder: {
    width: 80,
    height: 100,
    border: '1pt solid #BDC3C7',
    borderRadius: 2,
    backgroundColor: '#F8F9FA',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholderText: {
    fontSize: 8,
    color: '#7F8C8D',
    textAlign: 'center',
  },
  photoLabel: {
    fontSize: 7,
    color: '#7F8C8D',
    marginTop: 3,
    textAlign: 'center',
  },
  metaRow: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  metaLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#495057',
    width: '30%',
  },
  metaValue: {
    fontSize: 8,
    color: '#6C757D',
    width: '70%',
  },
  // Signature box styles
  signatureSection: {
    marginTop: 20,
    padding: 10,
    borderTop: '1pt solid #BDC3C7',
  },
  signatureBox: {
    border: '1pt solid #34495E',
    height: 30,
    width: 150,
    marginTop: 3,
    backgroundColor: '#FAFAFA',
    justifyContent: 'center',
    alignItems: 'center',
  },
  signatureLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#34495E',
    marginBottom: 3,
  },
  signatureDate: {
    fontSize: 7,
    color: '#7F8C8D',
  },
  candidateSignatureSection: {
    position: 'absolute',
    bottom: 70,
    right: 30,
    width: 160,
  },
  referenceSignatureBox: {
    width: 140,
    height: 25,
    border: '1pt solid #34495E',
    backgroundColor: '#FAFAFA',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
});

// Utility functions for PDF generation
export const pdfUtils = {
  // Helper function to detect if a file is uploaded
  isFileUploaded: (value, fieldName, formData) => {
    if (!value) {
      // Check if there's a corresponding field with "Id" suffix
      const idFieldName = `${fieldName}Id`;
      const idValue = formData[idFieldName];
      if (idValue && idValue !== '' && idValue !== null && idValue !== undefined) {
        return true;
      }
      return false;
    }
    
    // String value (file path, filename, base64, or ID)
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== 'undefined' && trimmed !== 'null';
    }
    
    // File object or file-like object
    if (typeof value === 'object' && value !== null) {
      // Standard File object properties
      if (value.name || value.fileName || value.size !== undefined || value.type) {
        return true;
      }
      // Base64 or data URL objects
      if (value.data || value.base64 || value.dataURL) {
        return true;
      }
      // Generic object with content
      if (Object.keys(value).length > 0) {
        return true;
      }
    }
    
    // Array of files
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
    
    return false;
  },

  // Format values based on field type
  formatValue: (value, field, formData = {}) => {
    if (!value || value === '' || value === null || value === undefined) {
      // For file fields, check if there's an ID field
      if (field?.type === 'file' && field?.name) {
        const hasFileId = pdfUtils.isFileUploaded(value, field.name, formData);
        return hasFileId ? 'Uploaded' : 'Not provided';
      }
      return 'Not provided';
    }
    
    // Debug logging for file fields (can be removed after testing)
    if (field?.type === 'file') {
      console.log(`File field ${field.name}:`, {
        value,
        type: typeof value,
        isArray: Array.isArray(value),
        keys: typeof value === 'object' ? Object.keys(value) : null,
        idField: `${field.name}Id`,
        idValue: formData[`${field.name}Id`]
      });
    }
    
    switch (field?.type) {
      case 'currency':
        const numValue = parseFloat(value);
        return isNaN(numValue) ? 'Invalid amount' : `Rs. ${numValue.toLocaleString('en-IN')}`;
      
      case 'select':
        if (field?.options) {
          const option = field.options.find(opt => opt.value === value);
          return option ? option.label : value;
        }
        return value;
      
      case 'file':
        return pdfUtils.isFileUploaded(value, field.name, formData) ? 'Uploaded' : 'Not provided';
      
      
      
      
      case 'textarea':
        return value.length > 100 ? `${value.substring(0, 100)}...` : value;
      
      case 'float':
        const floatValue = parseFloat(value);
        return isNaN(floatValue) ? value : floatValue.toString();
      
      case 'checkbox':
        return value ? 'Yes' : 'No';
      
      case 'email':
        return value;
      
      case 'url':
        return value;
      
      case 'tel':
        return value;
      
      case 'date':
        if (value) {
          try {
            return new Date(value).toLocaleDateString('en-IN');
          } catch (e) {
            return value;
          }
        }
        return 'Not provided';
      
      default:
        return value.toString();
    }
  },

  // Get current date formatted
  getCurrentDate: () => {
    return new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  // Get field value style based on type
  getFieldValueStyle: (value, field, styles) => {
    if (!value || value === '' || value === null || value === undefined) {
      return styles.emptyValue;
    }
    
    switch (field?.type) {
      case 'currency':
        return styles.currencyValue;
      case 'file':
        return styles.fileValue;
      default:
        return styles.fieldValue;
    }
  },

  // Generate safe filename
  generateFileName: (prefix, applicantName, id, extension = 'pdf') => {
    const safeName = (applicantName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');
    const date = new Date().toISOString().split('T')[0];
    return `${prefix}_${safeName}_${id || date}.${extension}`;
  },

  // Validate form data before PDF generation
  validateFormData: (formData, schema) => {
    const errors = [];
    
    if (!formData || Object.keys(formData).length === 0) {
      errors.push('No form data available');
    }
    
    if (!schema || !Array.isArray(schema) || schema.length === 0) {
      errors.push('No form schema available');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Fetch image with authorization and convert to base64
  fetchImageAsBase64: async (photoId, apiUrl, apiKey) => {
    if (!photoId || !apiUrl) {
      return null;
    }

    try {
      const response = await fetch(`${apiUrl}/Attachment/file/${photoId}`, {
        headers: {
          'x-api-key': apiKey,
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch image:', response.status, response.statusText);
        return null;
      }

      const blob = await response.blob();
      
      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  }
};

// Reusable PDF Header Component
export const PDFHeader = ({ title, subtitle, additionalInfo, logoSrc }) => (
  <View style={professionalPDFStyles.header}>
    {logoSrc && (
      <Image 
        src={logoSrc} 
        style={professionalPDFStyles.headerLogo} 
      />
    )}
    <Text style={professionalPDFStyles.headerTitle}>{title}</Text>
    <Text style={professionalPDFStyles.headerSubtitle}>{subtitle}</Text>
    <Text style={professionalPDFStyles.headerInfo}>
      Generated on: {pdfUtils.getCurrentDate()}
    </Text>
    {additionalInfo && (
      <Text style={professionalPDFStyles.headerInfo}>
        {additionalInfo}
      </Text>
    )}
  </View>
);

// Reusable PDF Footer Component
export const PDFFooter = ({ organizationName, additionalText }) => (
  <View style={professionalPDFStyles.footer} fixed>
    <Text style={professionalPDFStyles.footerText}>
      This document was generated electronically by {organizationName || 'the system'}
    </Text>
    {additionalText && (
      <Text style={professionalPDFStyles.footerText}>
        {additionalText}
      </Text>
    )}
  </View>
);

// Reusable PDF Page Number Component
export const PDFPageNumber = () => (
  <Text 
    style={professionalPDFStyles.pageNumber} 
    render={({ pageNumber, totalPages }) => 
      `Page ${pageNumber} of ${totalPages}`
    } 
    fixed 
  />
);

// Simple Candidate Signature Component (small box at bottom right)
export const CandidateSignatureBox = ({ applicantName }) => (
  <View style={professionalPDFStyles.candidateSignatureSection} fixed>
    <Text style={professionalPDFStyles.signatureLabel}>Candidate Signature:</Text>
    <View style={professionalPDFStyles.signatureBox}>
    </View>
  </View>
);

// Simple Reference Signature Component (small box on the right side)
export const ReferenceSignatureBox = () => (
  <View style={{ width: '30%' }}>
    <Text style={professionalPDFStyles.signatureLabel}>Reference Signature:</Text>
    <View style={professionalPDFStyles.referenceSignatureBox}>
    </View>
  </View>
);

// Passport Photo Component with error handling and authorization
export const PassportPhoto = ({ photoId, apiUrl, photoData }) => {
  if (!photoId && !photoData) {
    return (
      <View style={professionalPDFStyles.photoPlaceholder}>
        <Text style={professionalPDFStyles.photoPlaceholderText}>
          No Photo{'\n'}Uploaded
        </Text>
      </View>
    );
  }

  // If we have base64 photo data, use it directly
  if (photoData) {
    return (
      <Image 
        src={photoData}
        style={professionalPDFStyles.passportPhoto}
      />
    );
  }

  // For now, return a placeholder since we need to handle authorization
  // The actual image fetching will be done in the parent component
  return (
    <View style={professionalPDFStyles.photoPlaceholder}>
      <Text style={professionalPDFStyles.photoPlaceholderText}>
        Photo{'\n'}Available
      </Text>
    </View>
  );
};

export default {
  professionalPDFStyles,
  pdfUtils,
  PDFHeader,
  PDFFooter,
  PDFPageNumber,
  CandidateSignatureBox,
  ReferenceSignatureBox,
  PassportPhoto
};
